import type { ScaleRegion } from "@client/types/vision";
import { bilinearSample } from "./bilinearSample";

/**
 * F10 — Region scale (regional micropsia / macropsia).
 *
 * Uniformly shrinks or enlarges the image content inside an ellipse while
 * preserving its shape (no warp). A bridging falloff eases the effective scale
 * from full strength at the centre to 1 (no change) at the ellipse edge, so the
 * resized feature stays connected to the surrounding face instead of reading as
 * a pasted-on disc.
 */

/**
 * Radial falloff for the bridge edge.
 * @param d - normalised radial distance: 0 at the centre, 1 at the ellipse edge
 * @param bridge - transition-band width as a fraction of the radius (0–1)
 * @returns 1 in the full-strength core, smoothstep down to 0 at the edge
 */
export function regionScaleFalloff(d: number, bridge: number): number {
	if (d >= 1) return 0; // outside the ellipse: no effect
	if (bridge <= 0) return 1; // hard edge: full strength up to the boundary
	const inner = 1 - bridge;
	if (d <= inner) return 1; // full-strength core
	const u = (d - inner) / bridge; // 0..1 across the rim
	const s = u * u * (3 - 2 * u); // smoothstep
	return 1 - s;
}

/**
 * Apply region scale to image data.
 * @param imageData - source ImageData (not mutated)
 * @param region - ellipse in image space {cx, cy, rx, ry}
 * @param scale - scale multiplier (1 = no change; <1 shrinks; >1 enlarges)
 * @param bridge - edge softness 0–1 (0 = hard edge)
 * @returns new ImageData with the region scaled, background copied untouched
 */
export function applyRegionScale(
	imageData: ImageData,
	region: ScaleRegion,
	scale: number,
	bridge: number,
): ImageData {
	if (scale === 1) return imageData;

	const { width, height, data } = imageData;
	const output = new ImageData(width, height);
	output.data.set(data); // background outside the region stays untouched
	const out = output.data;

	const { cx, cy, rx, ry } = region;
	if (rx <= 0 || ry <= 0) return output;

	// Iterate the ellipse bounding box only, clamped to the image.
	const bx0 = Math.max(0, Math.floor(cx - rx));
	const by0 = Math.max(0, Math.floor(cy - ry));
	const bx1 = Math.min(width - 1, Math.ceil(cx + rx));
	const by1 = Math.min(height - 1, Math.ceil(cy + ry));

	for (let y = by0; y <= by1; y++) {
		for (let x = bx0; x <= bx1; x++) {
			const nx = (x - cx) / rx;
			const ny = (y - cy) / ry;
			const d = Math.sqrt(nx * nx + ny * ny);
			const t = regionScaleFalloff(d, bridge);
			if (t <= 0) continue; // rim/outside: leave the copied background

			// Effective scale eases to 1 at the edge → zero displacement → no seam.
			const eff = 1 + (scale - 1) * t;
			const srcX = cx + (x - cx) / eff;
			const srcY = cy + (y - cy) / eff;
			const [r, g, b, a] = bilinearSample(data, width, height, srcX, srcY);
			const i = (y * width + x) * 4;
			out[i] = r;
			out[i + 1] = g;
			out[i + 2] = b;
			out[i + 3] = a;
		}
	}
	return output;
}
